import { createBrowserRouter, RouterProvider } from "react-router-dom";
import LoginPage from "./LoginPage";
import IFrameAuthListener from "./IFrameAuthListener";
import RouteErrorPage from './RouteErrorPage';
import useMe from './hooks/useMe';
import useDataVerify from './dataHooks/useDataVerify';
import useDataMe from './dataHooks/useDataMe';
import useDataProfile from './dataHooks/useDataProfile';
import { appRootPath } from './utils';

// Private routes declare the useMe() flag that unlocks them (accessKey in App.js). Anyone verified
// by the shared hub session but without that flag gets the LoginPage, which (outside localhost)
// sends them back to the hub home.
const PrivateRoute = ({ element: Component, ...routeProps }) => {
  const me = useMe()
  const { accessKey, isQuestDashboard } = routeProps

  // The quest assessment dashboard is embedded as an iframe by the Quest portal. On the hub the
  // iframe shares the hub cookies, so a verified Quest or assessment user passes like any other
  // route; the postMessage handshake (IFrameAuthListener) remains the fallback for the legacy Quest
  // portal, whose iframe cannot see those cookies.
  if (isQuestDashboard) {
    const isQuestAssessmentDashboardAuthorized = (sessionStorage.getItem('isQuestAssessmentDashboardAuthorized') === 'true');
    const hasAccess = me.isVerified && (me.hasAssessmentAppAccess || me.hasQuestAccess)
    return (hasAccess || isQuestAssessmentDashboardAuthorized) ? <Component {...routeProps} /> : <IFrameAuthListener />
  }

  const hasAccess = me.isVerified && (accessKey ? Boolean(me[accessKey]) : true)
  return hasAccess ? <Component {...routeProps} /> : <LoginPage />;
};

const PublicRoute = ({ element: Component, ...routeProps }) => {
  return <Component {...routeProps} />;
};

export default ({
  routes = []
}) => {

  const { getIsVerified } = useDataVerify()
  const { fetchMe } = useDataMe()
  const { fetchProfile } = useDataProfile()

  const verifyLoader = async () => {
    const isVerified = await getIsVerified()
    const me = await fetchMe()
    // name / email / territory used to be readable login cookies; they now come from this
    // portal's own API, looked up for the verified user
    const profile = isVerified ? await fetchProfile(me.userId) : {}
    return { ...me, ...profile, isVerified }
  };

  const publicLoader  = async () => {
    const me = await fetchMe()
    return me;
  }

  const parseRoute = (route) => {
    return route.map((routeObj) => {
      const {
        path,
        isPrivate,
      } = routeObj;
      const parsedRouteObj = { ...routeObj }
      if (path === appRootPath) {
        parsedRouteObj.errorElement = <RouteErrorPage />;
      }

      if (isPrivate) {
        parsedRouteObj.element = <PrivateRoute {...routeObj} />;
        parsedRouteObj.loader = verifyLoader;
      } else {
        parsedRouteObj.element = <PublicRoute {...routeObj} />;
        parsedRouteObj.loader = publicLoader;
      }
      parsedRouteObj.shouldRevalidate = () => true;
      
      return parsedRouteObj
    })
  }

  const router = createBrowserRouter(parseRoute(routes));

  return <RouterProvider router={router} />
}
